import type {
  AddDelayType,
  AddGamePlayerType,
  AddPlayers
} from '@library/types/boardBuilder';
import { BoardBuilderGame } from '@bo-components/boardBuilder';
import { Button } from '@library/components/buttons';
import type { LeagueGames } from '@library/types/games';
import type { LeagueInfo } from '@library/types/leagues';
import { Loading } from '@library/components/Loading';
import { NoResults } from '@library/components/NoResults';
import { classNames } from '@library/utils';
import drawer from '@library/assets/images/icons/drawer.svg';
import { useState } from 'react';

export interface BoardBuilderGamesProps {
  games: LeagueGames;
  onSelectPlayer: ({ game, player }: AddGamePlayerType) => void;
  onUnSelectPlayer: ({ game, player }: AddGamePlayerType) => void;
  onAddDelay: ({ gameId, delay }: AddDelayType) => void;
  isLeagueGamesError: boolean;
  isLeagueGamesLoading: boolean;
  leagueInfo: LeagueInfo;
  searchValue: string;
  selectedDate: string;
  selectedGames: Array<string>;
  selectedPlayersList: AddPlayers;
  selectedPositions: Array<string>;
  selectedTeams: Array<string>;
}

export const BoardBuilderGames = ({
  games,
  onSelectPlayer,
  onUnSelectPlayer,
  onAddDelay,
  isLeagueGamesError,
  isLeagueGamesLoading,
  leagueInfo,
  searchValue,
  selectedDate,
  selectedGames,
  selectedPlayersList,
  selectedPositions,
  selectedTeams
}: BoardBuilderGamesProps) => {
  const [expandGamesView, setExpandGamesView] = useState(true);

  const filteredGames = selectedGames.length
    ? games.filter((game) => selectedGames.includes(game.game_id))
    : games;

  const displayGames = selectedTeams.length
    ? filteredGames.filter(
        (game) =>
          selectedTeams.includes(game.home_team) ||
          selectedTeams.includes(game.away_team)
      )
    : filteredGames;

  const boardBuilderGames = displayGames.map((game) => (
    <BoardBuilderGame
      key={game.game_id}
      game={game}
      leagueId={leagueInfo.id}
      onSelectPlayer={onSelectPlayer}
      onUnSelectPlayer={onUnSelectPlayer}
      onAddDelay={onAddDelay}
      searchValue={searchValue}
      selectedDate={selectedDate}
      selectedPositions={selectedPositions}
      selectedTeams={selectedTeams}
      selectedPlayersList={selectedPlayersList}
    />
  ));

  const isNoResults =
    !isLeagueGamesLoading && (isLeagueGamesError || !games.length);

  const showGames =
    !isLeagueGamesLoading && !isLeagueGamesError && games.length > 0;

  return (
    <section
      className={classNames(
        'bg-soFresh-110 border-soFresh-140 flex h-full shrink-0 flex-col items-start justify-start gap-4 overflow-scroll rounded-bl-md py-2 pl-6',
        expandGamesView ? 'w-[423px] border-r pr-4' : ''
      )}
    >
      <div className="font-heebo text-soClean-100 flex w-full items-center justify-between gap-4 pt-1 text-base font-medium leading-normal">
        <span className={!expandGamesView ? 'hidden' : ''}>Upcoming Games</span>
        <Button onClick={() => setExpandGamesView(!expandGamesView)}>
          <img
            className={classNames('h-6', !expandGamesView ? 'rotate-180' : '')}
            alt="open Global Rank drawer"
            src={drawer}
          />
        </Button>
      </div>
      {isLeagueGamesLoading && <Loading loadingMessage="Loading Games" />}
      {isNoResults && (
        <NoResults
          className="pr-6"
          label="No upcoming games for the selected criteria"
        />
      )}
      <div className={!expandGamesView ? 'hidden' : ''}>
        {showGames && boardBuilderGames}
      </div>
    </section>
  );
};
